import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  signal,
} from "@angular/core";

import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionDraftDetail,
} from "./platform-workflow-definition-lifecycle.models";
import { projectWorkflowDraftGraph } from "./workflow-draft-graph-projection";
import { WorkflowReadOnlyGraphComponent, type WorkflowGraphPresentationState } from "./workflow-read-only-graph.component";
import type {
  WorkflowReadOnlyGraphDecisionGateRequest,
  WorkflowReadOnlyGraphDecisionScopeRequest,
  WorkflowReadOnlyGraphSelectionRequest,
} from "./workflow-read-only-graph.models";

export interface WorkflowDraftGraphPresentationState {
  readonly version: 1;
  readonly ownerKey: string;
  readonly graph: WorkflowGraphPresentationState;
}

@Component({
  selector: "phms-platform-workflow-draft-graph-preview",
  standalone: true,
  imports: [CommonModule, WorkflowReadOnlyGraphComponent],
  templateUrl: "./platform-workflow-draft-graph-preview.component.html",
  styleUrl: "./platform-workflow-draft-graph-preview.component.scss",
})
export class PlatformWorkflowDraftGraphPreviewComponent {
  @ViewChild(WorkflowReadOnlyGraphComponent) private graphView?: WorkflowReadOnlyGraphComponent;
  private readonly documentState = signal<WorkflowDefinitionDocument | null>(null);

  @Input({ required: true }) draft: WorkflowDefinitionDraftDetail | null = null;

  @Input({ required: true })
  set document(value: WorkflowDefinitionDocument | null) {
    this.documentState.set(value);
  }

  get document(): WorkflowDefinitionDocument | null {
    return this.documentState();
  }

  @Input() recoveryCopyPending = false;
  @Input() recoveryLatestLoaded = false;
  @Input() sourceLoading = false;
  @Input() contextualInspector = false;
  @Input() inspectorStandalone = false;
  @Input() selectionControlled = false;
  @Input() selectedRendererElementId: string | null = null;

  @Output() readonly editNodeRequested = new EventEmitter<string>();
  @Output() readonly editEdgeRequested = new EventEmitter<string>();
  @Output() readonly selectionRequested = new EventEmitter<WorkflowReadOnlyGraphSelectionRequest>();
  @Output() readonly decisionGateRequested =
    new EventEmitter<WorkflowReadOnlyGraphDecisionGateRequest>();
  @Output() readonly decisionScopeRequested =
    new EventEmitter<WorkflowReadOnlyGraphDecisionScopeRequest>();

  readonly graph = computed(() => {
    const document = this.documentState();
    return document ? projectWorkflowDraftGraph(document) : null;
  });
  readonly hasPreviewWarnings = computed(() => {
    const graph = this.graph();
    return Boolean(graph && (
      graph.invalidElementCount > 0
      || graph.malformedEntryCount > 0
    ));
  });

  capturePresentationState(): WorkflowDraftGraphPresentationState | null {
    if (!this.draft || this.sourceLoading || !this.graphView
      || this.graphView.layoutStorageKey !== (this.contextualInspector ? `draft:${this.draft.id}` : null)
      || JSON.stringify(this.graphView.graph) !== JSON.stringify(this.graph())) return null;
    const graph = this.graphView.capturePresentationState();
    return graph ? { version: 1, ownerKey: this.presentationOwnerKey(), graph } : null;
  }

  restorePresentationState(state: WorkflowDraftGraphPresentationState): boolean {
    if (!state || state.version !== 1 || !this.draft || this.sourceLoading
      || state.ownerKey !== this.presentationOwnerKey() || !this.graphView
      || this.graphView.layoutStorageKey !== (this.contextualInspector ? `draft:${this.draft.id}` : null)
      || JSON.stringify(this.graphView.graph) !== JSON.stringify(this.graph())) return false;
    return this.graphView.restorePresentationState(state.graph);
  }

  private presentationOwnerKey(): string {
    return JSON.stringify([this.draft?.id, this.draft?.workflowCode, this.documentState()]);
  }

  requestNodeEdit(sourceId: string): void {
    this.editNodeRequested.emit(sourceId);
  }

  requestEdgeEdit(sourceId: string): void {
    this.editEdgeRequested.emit(sourceId);
  }

  requestSelection(request: WorkflowReadOnlyGraphSelectionRequest): void {
    this.selectionRequested.emit(request);
  }

  requestDecisionGate(request: WorkflowReadOnlyGraphDecisionGateRequest): void {
    this.decisionGateRequested.emit(request);
  }

  requestDecisionScope(request: WorkflowReadOnlyGraphDecisionScopeRequest): void {
    this.decisionScopeRequested.emit(request);
  }
}
